/**
 * Vereinfachte Tutorial-Schritte mit Fokus auf Erklärung statt strenger Interaktion
 * Diese Version funktioniert auch wenn nicht alle UI-Elemente getaggt sind
 */

import type { TutorialModuleDefinition, TutorialStep } from '@/app/types/tutorial';

// ============================================================================
// ISO.BLOCK TUTORIAL - Vereinfacht
// ============================================================================

const isoBlockSteps: TutorialStep[] = [
  {
    id: 'iso-welcome',
    title: 'Willkommen im ISO.BLOCK Editor! 🎉',
    description: 'Hier konstruieren Sie Ihre eigenen Bauteile aus geometrischen Grundformen und Sockets (Anschlusspunkte).\n\nDiese Bauteile können Sie später im BLOCK.SYSTEM Editor zu kompletten Anlagen zusammenbauen.\n\n👉 Das Tutorial führt Sie durch ALLE Werkzeuge Schritt für Schritt.',
    action: 'auto',
    tooltipPosition: 'center',
  },
  {
    id: 'iso-workflow',
    title: 'Der 3-stufige Workflow',
    description: '1️⃣ ISO.BLOCK: Bauteile konstruieren\n2️⃣ BLOCK.SYSTEM: Systeme bauen & drucken\n3️⃣ SYSTEM.TABLE: Stücklisten erstellen\n\nWir starten mit Schritt 1!',
    action: 'auto',
    tooltipPosition: 'center',
  },
  {
    id: 'iso-toolbar',
    title: 'Die Werkzeugleiste',
    description: 'In der oberen Leiste finden Sie alle Zeichenwerkzeuge:\n\n✏️ Linien, Kreise, Polygone\n📝 Text und Beschriftungen\n🔌 Sockets (Anschlusspunkte)\n\nWir gehen jetzt durch alle Werkzeuge!',
    action: 'auto',
    target: { selector: '[data-tutorial="iso-toolbar"]' },
    tooltipPosition: 'bottom',
  },
  {
    id: 'iso-tools-overview',
    title: 'Alle Zeichenwerkzeuge',
    description: '🖱️ **Select** (V): Elemente auswählen\n✏️ **Line** (L): Gerade Linien zeichnen\n⬟ **Polygon** (P): Vielecke und Rechtecke\n⭕ **Circle** (C): Kreise zeichnen\n📝 **Text** (T): Beschriftungen\n🔌 **Socket** (K): Anschlusspunkte\n\n💡 Tipp: Tastenkürzel in Klammern nutzen!',
    action: 'auto',
    tooltipPosition: 'center',
  },
  {
    id: 'iso-socket-importance',
    title: '⚠️ WICHTIG: Anschlusspunkte für Verbindungen',
    description: `Anschlusspunkte (Sockets) sind wie "Stecker" an Ihren Bauteilen.

✅ Bauteile MIT Anschlusspunkten können später verbunden werden
❌ Bauteile OHNE Anschlusspunkte bleiben isoliert

💡 Typische Konfigurationen:
   • 2+ Sockets: Durchflussbauteile (Ventil, Pumpe, etc.)
   • 0 Sockets: Dekorative Elemente, Beschriftungen, Messgeräte

📌 Ein Bauteil ohne Anschlusspunkte kann als Dekoration verwendet werden!`,
    action: 'auto',
    tooltipPosition: 'center',
  },
  {
    id: 'iso-drawing-demo',
    title: '📐 Übung: Einfaches L-Stück zeichnen',
    description: `Wir konstruieren jetzt zusammen ein einfaches L-Stück.

Sie sehen gestrichelte blaue Linien auf dem Canvas - das ist Ihre Vorlage!

🎯 Ziel: 2 Linien + 2 Sockets (wie die Vorlage)

👉 Klicken Sie auf "Weiter" wenn Sie bereit sind!`,
    action: 'auto',
    tooltipPosition: 'center',
    ghostPreview: [
      {
        type: 'line',
        points: [
          { u: 10, v: 10 },
          { u: 18, v: 10 }
        ],
        label: '1️⃣ Erste Linie (horizontal)'
      },
      {
        type: 'line',
        points: [
          { u: 18, v: 10 },
          { u: 18, v: 18 }
        ],
        label: '2️⃣ Zweite Linie (vertikal)'
      },
      {
        type: 'socket',
        center: { u: 10, v: 10 },
        label: '3️⃣ Socket links'
      },
      {
        type: 'socket',
        center: { u: 18, v: 18 },
        label: '4️⃣ Socket oben'
      }
    ],
  },
  {
    id: 'iso-drawing-step-1',
    title: 'Schritt 1: Line-Tool auswählen',
    description: `👉 Klicken Sie auf das Line-Tool in der Toolbar (oben).

💡 Tipp: Sie können auch die Taste "L" drücken!`,
    action: 'wait',
    target: { selector: '[data-tutorial="tool-line"]' },
    tooltipPosition: 'bottom',
    keyboardShortcut: 'L',
    keyboardHint: 'Line-Tool aktivieren',
    waitForEvent: { 
      type: 'tool-selected',
      payload: { tool: 'line' }
    },
    ghostPreview: [
      {
        type: 'line',
        points: [
          { u: 10, v: 10 },
          { u: 18, v: 10 }
        ],
        label: '1️⃣ Erste Linie'
      },
      {
        type: 'line',
        points: [
          { u: 18, v: 10 },
          { u: 18, v: 18 }
        ],
        label: '2️⃣ Zweite Linie'
      },
      {
        type: 'socket',
        center: { u: 10, v: 10 },
        label: '3️⃣'
      },
      {
        type: 'socket',
        center: { u: 18, v: 18 },
        label: '4️⃣'
      }
    ],
  },
  {
    id: 'iso-drawing-step-2',
    title: 'Schritt 2: Erste Linie zeichnen',
    description: `Super! Das Line-Tool ist jetzt aktiv.

👉 Zeichnen Sie jetzt die erste horizontale Linie:
   1. Klicken Sie für den Startpunkt
   2. Klicken Sie für den Endpunkt
   3. Drücken Sie ENTER oder ESC zum Abschließen

📍 Die gestrichelte Linie zeigt Ihnen die richtige Position!

Wenn fertig: Klicken Sie "Weiter"`,
    action: 'wait',
    target: { selector: '[data-tutorial="canvas-iso"]' },
    tooltipPosition: 'right',
    keyboardShortcut: 'Enter',
    keyboardHint: 'Linie abschließen',
    ghostPreview: [
      {
        type: 'line',
        points: [
          { u: 10, v: 10 },
          { u: 18, v: 10 }
        ],
        label: '1️⃣ Zeichnen Sie diese Linie'
      }
    ],
    waitForEvent: { 
      type: 'element-created',
      payload: { elementType: 'line' }
    }
  },
  {
    id: 'iso-drawing-step-3',
    title: 'Schritt 3: Zweite Linie zeichnen',
    description: `Gut gemacht! 

👉 Zeichnen Sie jetzt die zweite Linie (vertikal):
   1. Wählen Sie wieder das Line-Tool (falls nötig)
   2. Zeichnen Sie die vertikale Linie
   3. Drücken Sie ENTER zum Abschließen

📍 Folgen Sie der gestrichelten Vorlage!

Wenn fertig: Klicken Sie "Weiter"`,
    action: 'wait',
    target: { selector: '[data-tutorial="canvas-iso"]' },
    tooltipPosition: 'right',
    keyboardShortcut: 'L',
    keyboardHint: 'Line-Tool aktivieren',
    ghostPreview: [
      {
        type: 'line',
        points: [
          { u: 18, v: 10 },
          { u: 18, v: 18 }
        ],
        label: '2️⃣ Zeichnen Sie diese Linie'
      }
    ],
    waitForEvent: { 
      type: 'element-created',
      payload: { elementType: 'line' }
    }
  },
  {
    id: 'iso-drawing-step-4',
    title: 'Schritt 4: Socket-Tool auswählen',
    description: `Exzellent! Das L-Stück nimmt Form an!

Jetzt brauchen wir noch die Anschlusspunkte (Sockets).

👉 Klicken Sie auf das Socket-Tool in der Toolbar.

💡 Tipp: Oder drücken Sie die Taste "K"`,
    action: 'wait',
    target: { selector: '[data-tutorial="tool-socket"]' },
    tooltipPosition: 'bottom',
    keyboardShortcut: 'K',
    keyboardHint: 'Socket-Tool aktivieren',
    waitForEvent: { 
      type: 'tool-selected',
      payload: { tool: 'connection' }
    },
    ghostPreview: [
      {
        type: 'socket',
        center: { u: 10, v: 10 },
        label: '3️⃣ Socket hier'
      },
      {
        type: 'socket',
        center: { u: 18, v: 18 },
        label: '4️⃣ Socket hier'
      }
    ],
  },
  {
    id: 'iso-drawing-step-5',
    title: 'Schritt 5: Sockets platzieren',
    description: `Super! Das Socket-Tool ist aktiv.

👉 Platzieren Sie 2 Sockets an den Enden des L-Stücks:
   1. Klicken Sie auf das linke Ende
   2. Klicken Sie auf das obere Ende

📍 Die Kreuze zeigen die richtigen Positionen!

💡 Sockets sind wichtig - damit können Sie später Bauteile verbinden!

Wenn fertig: Klicken Sie "Weiter"`,
    action: 'wait',
    target: { selector: '[data-tutorial="canvas-iso"]' },
    tooltipPosition: 'right',
    keyboardShortcut: 'K',
    keyboardHint: 'Socket platzieren',
    ghostPreview: [
      {
        type: 'socket',
        center: { u: 10, v: 10 },
        label: '3️⃣ Klicken Sie hier'
      },
      {
        type: 'socket',
        center: { u: 18, v: 18 },
        label: '4️⃣ Klicken Sie hier'
      }
    ],
    waitForEvent: { 
      type: 'element-created',
      payload: { elementType: 'connection' }
    }
  },
  {
    id: 'iso-drawing-complete',
    title: '✅ Zeichnung abgeschlossen!',
    description: `Perfekt! Sie haben erfolgreich ein L-Stück konstruiert!

✅ 2 Linien gezeichnet
✅ 2 Sockets platziert
✅ Bereit für BLOCK.SYSTEM

Das Bauteil kann jetzt gespeichert werden (später im Tutorial).

👉 Klicken Sie "Weiter" um fortzufahren!`,
    action: 'auto',
    tooltipPosition: 'center',
  },
  {
    id: 'iso-properties',
    title: 'Properties Panel (rechts)',
    description: `Wenn Sie ein Element auswählen, sehen Sie rechts das Properties-Panel:

🎨 Farben ändern
📏 Größe anpassen
🔧 Eigenschaften bearbeiten

👉 Klicken Sie auf eines Ihrer gezeichneten Elemente!`,
    action: 'wait',
    target: { selector: '[data-tutorial="properties-panel"]' },
    tooltipPosition: 'left',
    waitForEvent: { 
      type: 'element-selected',
      payload: { count: 1 }
    }
  },
  {
    id: 'iso-attributes',
    title: '📋 Bauteil-Attribute definieren',
    description: `Attribute sind die technischen Daten Ihres Bauteils!

💡 **So definieren Sie Attribute:**

1. Drücken Sie ESC oder klicken Sie ins Leere (alle Elemente abwählen)
2. Im rechten Panel erscheint "Attribute"
3. Klicken Sie auf "+ Hinzufügen"
4. Definieren Sie:
   🔢 DN (Nennweite): z.B. 50mm
   🏗️ Material: z.B. Edelstahl
   📦 Artikelnummer: z.B. V-50-SS
   💰 Preis: z.B. 125.00 EUR

⚠️ Diese Daten erscheinen später in Stücklisten!

👉 Drücken Sie ESC um die Attribute zu sehen!`,
    action: 'wait',
    tooltipPosition: 'right',
    target: { selector: '[data-tutorial="properties-panel"]' },
    keyboardShortcut: 'ESC',
    keyboardHint: 'Alle Elemente abwählen',
  },
  {
    id: 'iso-save',
    title: 'Als IsomPart speichern',
    description: `Wenn Ihr Bauteil fertig ist:

1. Öffnen Sie das Hamburger-Menü (☰) links
2. Klicken Sie auf "Speichern"
3. Geben Sie einen Namen ein (z.B. "Ventil DN50")
4. Fertig! 🎉

👉 Öffnen Sie jetzt das Menü und klicken Sie auf Speichern!`,
    action: 'wait',
    tooltipPosition: 'center',
    waitForEvent: { 
      type: 'menu-opened',
      payload: { menu: 'save' }
    }
  },
  {
    id: 'iso-library',
    title: 'Symbol-Bibliothek',
    description: `In der Symbol-Bibliothek (☰ → Symbol-Bibliothek) finden Sie:

📚 Alle gespeicherten Bauteile
🔍 Suchfunktion
🗂️ Kategorien
📥 Import/Export

👉 Öffnen Sie jetzt die Symbol-Bibliothek!`,
    action: 'wait',
    tooltipPosition: 'center',
    waitForEvent: { 
      type: 'menu-opened',
      payload: { menu: 'library' }
    }
  },
  {
    id: 'iso-shortcuts',
    title: 'Wichtige Tastenkürzel',
    description: '⌨️ **Werkzeuge**:\nV = Select | L = Line | P = Polygon\nC = Circle | T = Text | K = Socket\n\n⌨️ **Aktionen**:\nStrg+Z = Undo | Strg+Y = Redo\nStrg+C = Copy | Strg+V = Paste\nEntf = Delete | ESC = Abbrechen\n\n⌨️ **Ansicht**:\nG = Grid toggle | H = Pan-Tool\nMausrad = Zoom',
    action: 'auto',
    tooltipPosition: 'center',
  },
  {
    id: 'iso-help-feedback',
    title: 'Hilfe & Feedback 💬',
    description: '❓ **Hilfe-Button**: Klicken Sie jederzeit auf das ❓ Symbol, um dieses Tutorial neu zu starten!\n\n💬 **Feedback-Button**: Haben Sie Fragen, Probleme oder Verbesserungsvorschläge? Nutzen Sie den Feedback-Button unten rechts!\n\n🎯 Ihr Feedback hilft uns, ISOCAD noch besser zu machen!',
    action: 'auto',
    tooltipPosition: 'center',
  },
  {
    id: 'iso-complete',
    title: 'ISO.BLOCK abgeschlossen! 🎉',
    description: 'Perfekt! Sie können jetzt eigene Bauteile konstruieren!\n\n✅ Gelernt: Alle Zeichenwerkzeuge\n✅ Gelernt: Sockets platzieren\n✅ Gelernt: Attribute definieren\n✅ Gelernt: Als IsomPart speichern\n\n➡️ Als nächstes: **BLOCK.SYSTEM** - Systeme bauen!',
    action: 'auto',
    tooltipPosition: 'center',
  },
];

// ============================================================================
// BLOCK.SYSTEM TUTORIAL - Vereinfacht
// ============================================================================

const blockSystemSteps: TutorialStep[] = [
  {
    id: 'bs-welcome',
    title: 'Willkommen im BLOCK.SYSTEM Editor! 🏗️',
    description: 'Hier bauen Sie aus Ihren Bauteilen komplette Systeme:\n\n🔧 Kühlkreisläufe\n💡 Elektrische Schaltungen\n🌡️ Heizungssysteme\n⚙️ Maschinelle Anlagen\n\nSie können Bauteile platzieren, verbinden, beschriften und ausdrucken!',
    action: 'auto',
    tooltipPosition: 'center',
  },
  {
    id: 'bs-workflow',
    title: 'Der BLOCK.SYSTEM Workflow',
    description: '1️⃣ Bauteile aus Library platzieren (Drag & Drop)\n2️⃣ Mit Leitungen verbinden (Pipe-Draw)\n3️⃣ Beschriften (Text & Labels)\n4️⃣ Planrahmen konfigurieren\n5️⃣ Drucken/PDF exportieren\n\nLos geht\'s!',
    action: 'auto',
    tooltipPosition: 'center',
  },
  {
    id: 'bs-library',
    title: 'Block-Library öffnen',
    description: 'Öffnen Sie die Block-Library über:\n\n☰ Hamburger-Menü → "Block-Bibliothek"\n\noder Tastenkürzel **B**\n\nHier sehen Sie alle IsomParts, die Sie im ISO.BLOCK Editor erstellt haben!',
    action: 'wait',
    tooltipPosition: 'center',
  },
  {
    id: 'bs-place-blocks',
    title: 'Bauteile platzieren (Drag & Drop)',
    description: '🖱️ **So geht\'s**:\n\n1. Öffnen Sie die Block-Library (B-Taste oder Menü)\n2. Wählen Sie ein Bauteil aus\n3. Ziehen Sie es auf die Canvas (Drag & Drop)\n4. Loslassen zum Platzieren\n\n👉 Platzieren Sie jetzt ein Bauteil!',
    action: 'wait',
    tooltipPosition: 'center',
    waitForEvent: { 
      type: 'action-performed',
      payload: { action: 'block-placed' }
    }
  },
  {
    id: 'bs-rotate',
    title: 'Bauteile rotieren',
    description: '🔄 **Rotieren**:\n\n• Bauteil auswählen\n• Taste **R** drücken\n• Oder Rotations-Buttons in Toolbar nutzen\n\nBauteile drehen sich um 90° im/gegen Uhrzeigersinn.\n\n💡 Positionieren Sie Ihre Bauteile so, dass die Sockets zueinander passen!',
    action: 'wait',
    tooltipPosition: 'center',
  },
  {
    id: 'bs-pipe-draw',
    title: 'Leitungen zeichnen (Pipe-Draw)',
    description: '🔌 **Pipe-Draw Tool aktivieren**:\n\nWählen Sie das Pipe-Draw Werkzeug in der Toolbar.\n\n**So verbinden Sie Bauteile**:\n1. Klicken Sie auf einen Socket von Bauteil 1\n2. Klicken Sie auf einen Socket von Bauteil 2\n3. Automatische Verbindung! 🎉\n\n👉 Zeichnen Sie jetzt eine Leitung!',
    action: 'wait',
    target: { selector: '[data-tutorial="tool-pipe-draw"]' },
    tooltipPosition: 'bottom',
    waitForEvent: { 
      type: 'action-performed',
      payload: { action: 'pipe-drawn' }
    }
  },
  {
    id: 'bs-connections',
    title: 'Automatische Verbindungserkennung',
    description: '🤖 **ISOCAD erkennt automatisch**:\n\n✅ Verbindungspunkte zwischen Bauteilen\n✅ DN-Werte (Durchmesser)\n✅ Zusammenhängende Systeme\n\nVerbundene Bauteile werden **farblich** als System gekennzeichnet!',
    action: 'wait',
    tooltipPosition: 'center',
  },
  {
    id: 'bs-systems',
    title: 'System-Verwaltung',
    description: '📊 **System-Panel**:\n\nÖffnen Sie das System-Panel (Sidebar rechts), um:\n\n🏷️ Systeme umzubenennen\n🎨 Systemfarbe ändern\n📝 System-Attribute hinzufügen:\n   • Medium (Wasser/Öl/Dampf)\n   • Druck (bar)\n   • Temperatur (°C)\n   • Volumenstrom (m³/h)',
    action: 'wait',
    tooltipPosition: 'center',
  },
  {
    id: 'bs-text-labels',
    title: 'Beschriftung: Text & Labels',
    description: '📝 **Zwei Arten von Beschriftungen**:\n\n**Text-Tool**:\n• Statische Beschriftungen\n• Freitext ("Hauptkreislauf", "Zulauf")\n\n**Label-Tool**:\n• Dynamische Beschriftungen\n• Automatische Daten aus Bauteilen:\n  {{blockId}}, {{dn}}, {{material}}\n\n💡 Labels sind SMART - sie aktualisieren sich automatisch!',
    action: 'wait',
    tooltipPosition: 'center',
  },
  {
    id: 'bs-subsystems',
    title: 'Subsysteme (Optional)',
    description: '🔄 **Subsysteme** = wiederverwendbare Bauteil-Gruppen\n\n**Erstellen**:\n1. Mehrere Blöcke auswählen (Shift+Klick)\n2. "Create Subsystem" in Toolbar klicken\n3. Namen vergeben\n4. Fertig!\n\n👉 Versuchen Sie es! (Optional - oder klicken Sie "Weiter")',
    action: 'wait',
    target: { selector: '[data-tutorial="create-subsystem-btn"]' },
    tooltipPosition: 'bottom',
    waitForEvent: { 
      type: 'action-performed',
      payload: { action: 'subsystem-created' }
    }
  },
  {
    id: 'bs-parameters',
    title: 'Parameter-Management',
    description: '⚙️ **Globale Parameter**:\n\nDefinieren Sie projekt-weite Parameter:\n\n📄 Projekt-Nummer\n🏢 Kunde/Firma\n📍 Standort\n📅 Datum\n👤 Bearbeiter\n\nDiese erscheinen automatisch in:\n• Labels\n• Titelblöcken\n• Stücklisten',
    action: 'wait',
    tooltipPosition: 'center',
  },
  {
    id: 'bs-view-settings',
    title: 'Ansicht & Darstellung',
    description: '👁️ **View Settings**:\n\n🎨 Farben anpassen\n📏 Linienstärken\n🔍 Zoom-Stufen\n📐 Grid-Einstellungen\n\n**Background Settings**:\n\n⬜ Hintergrundfarbe\n📊 Grid-Größe\n🧲 Snap-Verhalten',
    action: 'wait',
    tooltipPosition: 'center',
  },
  {
    id: 'bs-plan-layout',
    title: 'Plan-Layout & Titelblock 📄',
    description: '📋 **Planrahmen konfigurieren**:\n\nÖffnen Sie "Plan-Layout" um:\n\n📏 Format wählen (A4/A3/A2/A1)\n🔄 Orientierung (Hoch/Quer)\n🏢 Firma & Logo\n📝 Projekt-Informationen\n\n⚠️ Der Titelblock erscheint beim Drucken auf JEDEM Blatt!\n\n💾 Als Template speichern für Wiederverwendung!',
    action: 'wait',
    tooltipPosition: 'center',
  },
  {
    id: 'bs-save-export',
    title: 'Speichern & Exportieren',
    description: '💾 **Speichern**:\n\n• Auto-Save im LocalStorage\n• Manuell: ☰ → "Speichern"\n• Als JSON exportieren (Backup!)\n\n📤 **Export-Optionen**:\n\n• JSON (für Backup/Transfer)\n• SVG (Vektor-Grafik)\n• PDF (Druck)',
    action: 'wait',
    tooltipPosition: 'center',
  },
  {
    id: 'bs-print',
    title: 'Drucken & PDF erstellen 🖨️',
    description: '🖨️ **Print-Dialog öffnen**:\n\n1. Print-Button in Toolbar\n2. Format wählen (A4/A3/A2/A1)\n3. Orientierung wählen\n4. Maßstab einstellen\n5. Vorschau prüfen\n6. Drucken oder als PDF speichern\n\n✅ Ihr Titelblock wird automatisch eingefügt!',
    action: 'wait',
    tooltipPosition: 'center',
  },
  {
    id: 'bs-selection-filter',
    title: 'Bonus: Selection-Filter',
    description: '🔍 **Selection-Filter**:\n\nFiltern Sie Ihre Auswahl nach:\n\n☑️ Nur Blöcke\n☑️ Nur Verbindungen\n☑️ Nur Text\n☑️ Nur Labels\n\nNützlich bei komplexen Plänen mit vielen Elementen!',
    action: 'wait',
    tooltipPosition: 'center',
  },
  {
    id: 'bs-shortcuts',
    title: 'Wichtige Tastenkürzel',
    description: '⌨️ **BLOCK.SYSTEM Shortcuts**:\n\nV = Select | H = Pan | B = Block-Library\nR = Rotate | Strg+D = Duplicate\nEntf = Delete | ESC = Deselect\n\nStrg+Z = Undo | Strg+Y = Redo\nStrg+S = Save | Strg+P = Print',
    action: 'auto',
    tooltipPosition: 'center',
  },
  {
    id: 'bs-complete',
    title: 'BLOCK.SYSTEM abgeschlossen! 🎉',
    description: 'Hervorragend! Sie können jetzt:\n\n✅ Bauteile platzieren & verbinden\n✅ Systeme erkennen & verwalten\n✅ Beschriften mit Text & Labels\n✅ Planrahmen konfigurieren\n✅ Drucken & PDF erstellen\n\n➡️ Als nächstes: **SYSTEM.TABLE** - Stücklisten!',
    action: 'auto',
    tooltipPosition: 'center',
  },
];

// ============================================================================
// SYSTEM.TABLE TUTORIAL - Vereinfacht
// ============================================================================

const systemTableSteps: TutorialStep[] = [
  {
    id: 'st-welcome',
    title: 'Willkommen im SYSTEM.TABLE Editor! 📊',
    description: 'Hier werten Sie Ihre Systeme aus:\n\n📋 Stücklisten erstellen\n📊 Materiallisten generieren\n💰 Kosten berechnen\n🖨️ Professionelle Tabellen drucken\n\nAlle Daten aus ISO.BLOCK & BLOCK.SYSTEM fließen hier zusammen!',
    action: 'auto',
    tooltipPosition: 'center',
  },
  {
    id: 'st-table-view',
    title: 'Die Tabellen-Ansicht',
    description: '📊 **Was Sie sehen**:\n\nJede Zeile = Ein System/Block\nJede Spalte = Ein Attribut\n\n🔍 Sie sehen automatisch:\n• Block-IDs\n• Typ/Kategorie\n• Alle definierten Attribute\n• Verbindungen\n\nDie Tabelle ist **live** - Änderungen werden sofort angezeigt!',
    action: 'wait',
    tooltipPosition: 'center',
  },
  {
    id: 'st-columns',
    title: 'Spalten hinzufügen & konfigurieren',
    description: '➕ **Spalten verwalten**:\n\n1. "Spalte hinzufügen" klicken\n2. Attribut wählen (DN, Material, Preis...)\n3. Breite festlegen\n4. Format definieren (Text/Zahl/Datum)\n\n✏️ Spalten können:\n• Umbenannt werden\n• Verschoben werden\n• Gelöscht werden\n• Sortiert werden',
    action: 'wait',
    tooltipPosition: 'center',
  },
  {
    id: 'st-editing',
    title: 'Zellen editieren',
    description: '✏️ **Direkte Bearbeitung**:\n\nDoppelklick auf Zelle → Wert ändern\n\nÄnderungen werden:\n✅ Sofort gespeichert\n✅ In BLOCK.SYSTEM synchronisiert\n✅ In allen Tabellen aktualisiert\n\n💡 Nutzen Sie Tab/Enter zum Navigieren zwischen Zellen!',
    action: 'wait',
    tooltipPosition: 'center',
  },
  {
    id: 'st-header-merge',
    title: 'Header zusammenführen',
    description: '🔗 **Übersichtliche Gruppierung**:\n\nMehrere Spalten unter einem Header:\n\n"Technische Daten"\n  ├─ DN\n  ├─ Druck\n  └─ Temperatur\n\n"Kaufmännische Daten"\n  ├─ Preis\n  ├─ Lieferant\n  └─ Artikelnr\n\nSieht professionell aus und erhöht Übersicht!',
    action: 'wait',
    tooltipPosition: 'center',
  },
  {
    id: 'st-borders',
    title: 'Rahmen & Formatierung',
    description: '🖼️ **Border-Presets**:\n\nNutzen Sie vordefinierte Rahmen:\n\n📋 DIN 5008 (deutsches Geschäftsschreiben)\n📊 Excel-Style\n📄 Simple Grid\n🎨 Custom\n\n**Area-Dialog**:\nFormatieren Sie spezifische Bereiche:\n• Farben\n• Schriftarten\n• Rahmen\n• Abstände',
    action: 'wait',
    tooltipPosition: 'center',
  },
  {
    id: 'st-grouping',
    title: 'Gruppierung & Summen',
    description: '📁 **Gruppierung**:\n\nGruppieren Sie Zeilen nach:\n• System-Typ\n• Material\n• Kategorie\n• Lieferant\n\n➕ **Summen-Zeile**:\n\nAutomatische Berechnung:\n• Gesamt-Längen\n• Stückzahlen\n• Gesamtkosten\n• Durchschnittswerte',
    action: 'wait',
    tooltipPosition: 'center',
  },
  {
    id: 'st-project-attrs',
    title: 'Projekt-Attribute',
    description: '📝 **Globale Projekt-Daten**:\n\nDefinieren Sie projekt-weite Attribute:\n\n📄 Projekt-Nr: P-2026-001\n🏢 Kunde: Musterfirma GmbH\n📍 Standort: Berlin\n📅 Datum: 28.02.2026\n👤 Bearbeiter: Max Mustermann\n\nDiese erscheinen in:\n• Tabellen-Header\n• PDF-Export\n• Druckausgabe',
    action: 'wait',
    tooltipPosition: 'center',
  },
  {
    id: 'st-print-export',
    title: 'Drucken & Exportieren 🖨️',
    description: '🖨️ **Druck-Optionen**:\n\nFormat: A4/A3/A2/A1\nOrientierung: Hoch/Quer\nSeitenumbruch: Automatisch\n\n**Export-Formate**:\n\n📊 Excel (.xlsx)\n📄 CSV\n📋 PDF\n\n💡 **Tipp**: Speichern Sie Ihre Konfiguration als Preset!',
    action: 'wait',
    tooltipPosition: 'center',
  },
  {
    id: 'st-presets',
    title: 'Presets & Templates',
    description: '💾 **Konfiguration speichern**:\n\nSpeichern Sie Ihre Tabellen-Konfiguration:\n\n📋 Spalten-Layout\n🎨 Formatierung\n🖼️ Rahmen\n📁 Gruppierung\n\nAls **Preset** für zukünftige Projekte!\n\n⚡ Schneller Zugriff auf häufig genutzte Layouts!',
    action: 'wait',
    tooltipPosition: 'center',
  },
  {
    id: 'st-complete',
    title: 'SYSTEM.TABLE abgeschlossen! 🎉',
    description: '**Sie beherrschen jetzt alle drei Module!** 🚀\n\n✅ ISO.BLOCK: Bauteile konstruieren\n✅ BLOCK.SYSTEM: Systeme bauen\n✅ SYSTEM.TABLE: Stücklisten erstellen\n\n🎓 Sie sind jetzt ISOCAD-Experte!\n\n💡 Tipp: Tutorials jederzeit über ❓ Button neu starten!',
    action: 'auto',
    tooltipPosition: 'center',
  },
];

// ============================================================================
// EXPORT
// ============================================================================

export function getTutorialModules(): TutorialModuleDefinition[] {
  return [
    {
      id: 'iso-block',
      name: 'ISO.BLOCK - Bauteil-Konstruktion',
      description: 'Lernen Sie, wie Sie eigene Bauteile aus Linien, Kreisen, Polygonen und Sockets konstruieren und als IsomPart speichern.',
      estimatedTime: '8-10 Minuten',
      steps: isoBlockSteps,
    },
    {
      id: 'block-system',
      name: 'BLOCK.SYSTEM - System-Konstruktion',
      description: 'Bauen Sie komplette Systeme aus Bauteilen, verbinden Sie diese mit Leitungen, beschriften Sie und drucken Sie Ihre Pläne.',
      estimatedTime: '12-15 Minuten',
      steps: blockSystemSteps,
    },
    {
      id: 'system-table',
      name: 'SYSTEM.TABLE - Auswertung & Stücklisten',
      description: 'Werten Sie Ihre Systeme aus, erstellen Sie professionelle Stücklisten und Materiallisten.',
      estimatedTime: '6-8 Minuten',
      steps: systemTableSteps,
    },
  ];
}

export function getTutorialModule(id: string): TutorialModuleDefinition | undefined {
  return getTutorialModules().find(m => m.id === id);
}